















































import { Vue, Component, Prop, Watch } from 'vue-property-decorator';

interface ScratchCardPreviewConfig {
  position?: string;
  /** 刮奖前图（涂层）：刮开前覆盖在奖项上的图片，优先于 bgColor */
  coatImage?: string;
  /** 刮奖底图（奖项背景）：刮开后展示奖项内容的背景图片，优先于 bgColor */
  awardBgImage?: string;
  /** 底图颜色：同时用于刮奖遮罩与底图默认背景色（未上传图片时生效） */
  bgColor?: string;
  text?: string;
  fontFamily?: string;
  color?: string;
  fontSize?: string;
}

@Component
export default class ScratchCardPreview extends Vue {
  @Prop({ type: Object, default: () => ({}) }) config!: ScratchCardPreviewConfig;
  /** 未设置底图时使用的兜底背景色（取弹窗背景框颜色） */
  @Prop({ type: String, default: '' }) fallbackBgColor!: string;
  /** B 端预览设备态：移动端由预览切换控制，不依赖真实浏览器 media */
  @Prop({ type: String, default: 'desktop' }) device!: string;

  /** 预览态：false=未刮开（涂层覆盖），true=已刮开（展示奖项底图） */
  revealed = false;
  /** 涂层（前图/coatImage）是否已实打实画到画布上。
   *  false 期间用底色遮罩盖住整块区域，避免下方奖项层（后图）先被看到——对齐 C 端 GuaGuaLe.coatPainted。
   *  涂层图加载是异步的，onload 完成并 drawImage 后才置 true 撤遮罩；无涂层图/解码失败时置底色即视为已就绪。 */
  coatPainted = false;
  /** 手势引导：预览首次刮擦后隐藏，避免持续遮挡刮奖体验 */
  gestureGuideVisible = true;

  // ---- 涂鸦刮卡相关状态 ----
  /** 刮开笔触半径：直径 50px 的圆形路径 */
  private readonly BRUSH_RADIUS = 25;
  /** 覆盖度统计网格单元（与笔触直径同尺寸） */
  private readonly CELL = 25;
  /** 揭晓过渡时长（ms）：达标后整层涂层 opacity 1→0 的过渡时长。
   *  0 = 关闭过渡动画，达标后瞬间揭晓（直接隐去涂层露出奖项）；
   *  >0 = 按该时长做 CSS transition 渐隐。 */
  private readonly FADE_DURATION_MS = 0;
  private isDrawing = false;
  private lastX = 0;
  private lastY = 0;
  private coveredCells: Set<string> = new Set();
  private totalCells = 1;
  private ctx: CanvasRenderingContext2D | null = null;
  /** 涂层图片异步加载序号：尺寸/图片变化重绘后，旧 onload 不允许回写当前画布 */
  private coatDrawToken = 0;
  // ---- 揭晓状态（需求：去掉颗粒溶解，改为整层透明度揭晓；时长由 FADE_DURATION_MS 控制）----
  /** 揭晓进行中：从触发到达标后置 revealed 期间禁止继续刮 */
  private revealing = false;
  /** 揭晓收尾 setTimeout id：组件销毁/换图时清除，避免回调访问已卸载 canvas */
  private revealTimer = 0;

  /** 当前已生效的画布像素尺寸；尺寸未变时跳过无谓重绘 */
  private currentW = 0;
  private currentH = 0;
  /** 监听容器尺寸变化（图片加载、iframe 高度、过渡动画、响应式都会触发），保证画布始终填满 */
  private resizeObserver: ResizeObserver | null = null;

  /**
   * 用于撑起容器高度的占位图：优先前图（刮开前的主视觉，比例必须精确），
   * 未上传前图时退回底图；都没有则走 DOM 里的空占位（正方形兜底）。
   */
  get sizerImage(): string {
    return this.config.coatImage || this.config.awardBgImage || '';
  }

  /**
   * 占位图加载完成 / 失败都会改变容器高度，此时重新测量并重绘涂层。
   * （ResizeObserver 也会兜底，这里主动触发一次，避免首屏少一帧空白）
   */
  onSizerChange(): void {
    this.$nextTick(() => this.initCanvas());
  }

  get displayText(): string {
    return this.config.text || '';
  }

  /** 加载遮罩底色：优先底图颜色 bgColor，回退弹窗背景框颜色（对齐 C 端 bgColor || '#C8A48C'） */
  get coatLoadingColor(): string {
    return this.config.bgColor || this.fallbackBgColor || '#C8A48C';
  }

  get fontSizePx(): string {
    const v = this.config.fontSize;
    if (!v) return '22px';
    // 新数据：直接是 px 数值（如 "24px"），与弹窗其它文本字号一致
    if (typeof v === 'string' && v.endsWith('px')) return v;
    // 兼容历史 small / middle / large 文案档位
    switch (v) {
      case 'small':
        return '16px';
      case 'large':
        return '30px';
      case 'middle':
      default:
        return '22px';
    }
  }

  get awardStyle(): Record<string, string> {
    const awardImg = this.config.awardBgImage;
    const bg = this.config.bgColor;
    return {
      backgroundImage: awardImg ? `url(${awardImg})` : 'none',
      backgroundColor: awardImg ? (bg || 'transparent') : (bg || this.fallbackBgColor || '#F3E7D9'),
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
  }

  get textStyle(): Record<string, string> {
    return {
      fontFamily: this.config.fontFamily || 'inherit',
      color: this.config.color || '#A86A3D',
      fontSize: this.fontSizePx,
    };
  }

  mounted(): void {
    // 容器高度由占位图撑起，挂载后下一帧再量尺寸更稳
    this.$nextTick(() => this.initCanvas());
    // 无论首帧是否量到尺寸都挂上监听：图片加载、窗口缩放、iframe 高度变化都要重算画布
    this.ensureResizeObserver();
    window.addEventListener('mouseup', this.endScratch);
    window.addEventListener('touchend', this.endScratch);
  }

  beforeDestroy(): void {
    this.coatDrawToken += 1;
    if (this.revealTimer) clearTimeout(this.revealTimer);
    this.revealTimer = 0;
    window.removeEventListener('mouseup', this.endScratch);
    window.removeEventListener('touchend', this.endScratch);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  /** 涂层变化（改色或替换图片）时重置刮卡状态并重绘 */
  @Watch('config.bgColor')
  @Watch('config.coatImage')
  @Watch('config.awardBgImage')
  onCoatChange(): void {
    this.revealed = false;
    this.coatPainted = false;
    this.gestureGuideVisible = true;
    this.coveredCells.clear();
    if (this.revealTimer) clearTimeout(this.revealTimer);
    this.revealTimer = 0;
    this.revealing = false;
    // 强制重绘涂层：置零当前尺寸，使 applySize 即使尺寸不变也会重新绘制
    this.currentW = 0;
    this.currentH = 0;
    this.$nextTick(() => this.initCanvas());
  }

  /** 测量 canvas 真实渲染尺寸（getBoundingClientRect 取 CSS 像素），
   *  让画布缓冲尺寸 = 显示尺寸，避免缓冲/显示比例不一致被 CSS 拉伸导致压扁 */
  private measure(): { w: number; h: number } {
    const canvas = this.$refs.canvas as HTMLCanvasElement | undefined;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const w = Math.round(rect.width);
      const h = Math.round(rect.height);
      if (w > 0 && h > 0) return { w, h };
    }
    const el = this.$el as HTMLElement;
    return { w: el.clientWidth, h: el.clientHeight };
  }

  private initCanvas(): void {
    const canvas = this.$refs.canvas as HTMLCanvasElement | undefined;
    if (!canvas) return;
    const { w, h } = this.measure();
    if (w === 0 || h === 0) {
      // 尺寸尚未就绪，交给 ResizeObserver 或下一帧轮询处理
      this.ensureResizeObserver();
      return;
    }
    this.applySize(w, h);
  }

  /** 确保已挂载尺寸监听；ResizeObserver 不可用时回退到 rAF 轮询 */
  private ensureResizeObserver(): void {
    if (this.resizeObserver) return;
    if (typeof ResizeObserver === 'undefined' || !this.$el) {
      requestAnimationFrame(() => this.initCanvas());
      return;
    }
    const el = this.$el as HTMLElement;
    this.resizeObserver = new ResizeObserver(() => {
      const { w, h } = this.measure();
      if (w === 0 || h === 0) return;
      this.applySize(w, h);
    });
    this.resizeObserver.observe(el);
  }

  /** 设置画布缓冲尺寸并重绘涂层。画布尺寸变化会清空内容，必须重绘 */
  private applySize(w: number, h: number, force = false): void {
    const canvas = this.$refs.canvas as HTMLCanvasElement | undefined;
    if (!canvas) return;
    // 尺寸未变且已有上下文则跳过，避免无谓重绘（ResizeObserver 会频繁回调）
    if (!force && w === this.currentW && h === this.currentH && this.ctx) return;
    // 尺寸/内容重置会清空画布：若此刻正在揭晓，先停掉收尾定时器，重绘后回到完整涂层态，避免半途被打断
    if (this.revealTimer) clearTimeout(this.revealTimer);
    this.revealTimer = 0;
    this.revealing = false;
    this.currentW = w;
    this.currentH = h;
    canvas.width = w;
    canvas.height = h;
    this.ctx = canvas.getContext('2d');
    this.totalCells = Math.ceil(w / this.CELL) * Math.ceil(h / this.CELL);
    this.coveredCells.clear();
    this.drawCoat(w, h);
    // 重置揭晓残留样式：换图/尺寸变化后涂层恢复不透明（清掉可能残留的过渡与透明）
    canvas.style.transition = 'none';
    canvas.style.opacity = '1';
  }

  /** 绘制涂层：先同步铺一层不透明底色（画布任何时刻都不透明，不会漏出下方奖项层），
   *  再把涂层图按 cover 比例画上去；无涂层图/解码失败时保持底色。
   *  涂层图异步加载，onload 完成后才置 coatPainted=true 撤遮罩——对齐 C 端 GuaGuaLe.drawCoat */
  private drawCoat(w: number, h: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const drawToken = ++this.coatDrawToken;
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, w, h);
    // 先铺底：底色打底，涂层图没到位时画布也不是透明的
    ctx.fillStyle = this.config.bgColor || '#C8A48C';
    ctx.fillRect(0, 0, w, h);
    const coat = this.config.coatImage;
    if (coat) {
      const img = new Image();
      img.onload = () => {
        if (drawToken !== this.coatDrawToken) return;
        if (!img.width || !img.height) {
          // 图异常：底色已铺，视为就绪
          this.coatPainted = true;
          return;
        }
        // 短边撑满、居中裁剪：只裁不拉，图片本身比例不变
        const scale = Math.max(w / img.width, h / img.height);
        const dw = img.width * scale;
        const dh = img.height * scale;
        const dx = (w - dw) / 2;
        const dy = (h - dh) / 2;
        ctx.drawImage(img, dx, dy, dw, dh);
        this.coatPainted = true;
      };
      img.onerror = () => {
        if (drawToken !== this.coatDrawToken) return;
        // 解码失败按纯色处理：底色已铺，视为就绪
        this.coatPainted = true;
      };
      img.src = coat;
    } else {
      // 无涂层图：底色即涂层，视为就绪
      this.coatPainted = true;
    }
  }

  private getPos(e: MouseEvent | TouchEvent): { x: number; y: number } {
    const canvas = this.$refs.canvas as HTMLCanvasElement;
    const rect = canvas.getBoundingClientRect();
    const point = 'touches' in e && e.touches.length ? e.touches[0] : (e as MouseEvent);
    return {
      x: point.clientX - rect.left,
      y: point.clientY - rect.top,
    };
  }

  private startScratch(e: MouseEvent | TouchEvent): void {
    if (this.revealed || this.revealing) return;
    const { x, y } = this.getPos(e);
    this.gestureGuideVisible = false;
    this.isDrawing = true;
    this.lastX = x;
    this.lastY = y;
    this.erase(x, y);
  }

  private scratchMove(e: MouseEvent | TouchEvent): void {
    if (!this.isDrawing || this.revealed || this.revealing) return;
    const { x, y } = this.getPos(e);
    this.eraseLine(this.lastX, this.lastY, x, y);
    this.lastX = x;
    this.lastY = y;
    // 拖动过程中实时判定，达到阈值立即露出底图，避免松手前已刮够却看不到
    this.checkReveal();
  }

  private endScratch(): void {
    if (!this.isDrawing) return;
    this.isDrawing = false;
    this.checkReveal();
  }

  /** 单点圆形擦除（直径 50px） */
  private erase(x: number, y: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, this.BRUSH_RADIUS, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
    this.markCells(x, y);
  }

  /** 两点之间用圆形笔触连线，保证连续滑动不断笔 */
  private eraseLine(x0: number, y0: number, x1: number, y1: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    ctx.globalCompositeOperation = 'destination-out';
    ctx.lineWidth = this.BRUSH_RADIUS * 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.stroke();
    ctx.globalCompositeOperation = 'source-over';
    // 沿线按笔触直径采样标记覆盖网格
    const dist = Math.hypot(x1 - x0, y1 - y0);
    const steps = Math.max(1, Math.ceil(dist / this.BRUSH_RADIUS));
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      this.markCells(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t);
    }
  }

  /** 标记笔触覆盖到的网格单元：以「格心是否落在笔触圆内」判定，避免低估真实覆盖面积 */
  private markCells(x: number, y: number): void {
    const r = this.BRUSH_RADIUS;
    const cell = this.CELL;
    const minCx = Math.floor((x - r) / cell);
    const maxCx = Math.floor((x + r) / cell);
    const minCy = Math.floor((y - r) / cell);
    const maxCy = Math.floor((y + r) / cell);
    for (let cx = minCx; cx <= maxCx; cx++) {
      for (let cy = minCy; cy <= maxCy; cy++) {
        const ccx = (cx + 0.5) * cell;
        const ccy = (cy + 0.5) * cell;
        if ((ccx - x) ** 2 + (ccy - y) ** 2 <= r * r) {
          this.coveredCells.add(`${cx},${cy}`);
        }
      }
    }
  }

  /** 覆盖过 40% 即触发揭晓（对齐 C 端阈值；过渡时长与开关由 FADE_DURATION_MS 控制：0 瞬间、>0 渐隐） */
  private checkReveal(): void {
    if (this.revealed || this.revealing) return;
    if (this.coveredCells.size >= this.totalCells * 0.4) {
      this.doReveal();
    }
  }

  /**
   * 整层揭晓：达标后把涂层整层 opacity 置 0，露出下方常显的奖项层。
   * 过渡由 FADE_DURATION_MS 控制：0 = 无动画、下一帧直接隐去（当前配置）；
   * >0 = CSS transition 按该时长渐隐（linear 匀速）。
   * 期间 revealing 守卫禁止继续刮；收尾定时器到点后置 revealed 卸载 canvas。
   * 需求：去掉颗粒溶解、改为整层揭晓，且允许关闭过渡动画（0）。
   */
  private doReveal(): void {
    const canvas = this.$refs.canvas as HTMLCanvasElement | undefined;
    if (!canvas) {
      this.revealed = true;
      return;
    }
    this.revealing = true;
    // 下一帧（样式提交后）再置 opacity：>0 时确保 transition 从当前 1 起跳；
    // =0 时等价于渲染一帧后直接隐去，避免首帧被合并成无意义的瞬变
    this.$nextTick(() => {
      const c = this.$refs.canvas as HTMLCanvasElement | undefined;
      if (!c) {
        this.revealed = true;
        return;
      }
      c.style.transition = `opacity ${this.FADE_DURATION_MS}ms linear`;
      // 强制 reflow 让 transition 在改 opacity 前已注册，>0 时才不会丢失过渡
      void c.offsetWidth;
      c.style.opacity = '0';
    });
    // 到点后卸载涂层（时长与 FADE_DURATION_MS 对齐，0 也走异步一帧收尾，避免同步卸载打断样式提交；
    // 换图/销毁时由 onCoatChange/beforeDestroy 清除）
    this.revealTimer = window.setTimeout(() => {
      this.revealing = false;
      this.revealed = true;
    }, this.FADE_DURATION_MS);
  }
}
